// src/components/auth/ProtectedRoute.jsx
import { motion } from "framer-motion";
import { AlertTriangle, Car, UserCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import Login from "./Login";

export default function ProtectedRoute({ 
  children, 
  requiredRole = null, 
  fallback = null 
}) {
  const { user, profile, loading, isAdmin, isOperator } = useAuth();

  // NEW: Check if user is viewer
  const isViewer = profile?.role === 'viewer';

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Car className="w-8 h-8 text-white" />
          </div>
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Caricamento...</p>
        </motion.div>
      </div>
    );
  }

  // If not authenticated, show login
  if (!user || !profile) {
    return <Login />;
  }

  // Check if user is "registrato" - they can't access anything except this message
  if (profile.role === 'registrato') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-20 h-20 bg-gradient-to-br from-gray-500 to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <UserCheck className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-slate-800 mb-4">
            Account in Attesa di Attivazione
          </h1>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-yellow-800 mb-2">
              Il tuo account è stato registrato con successo, ma è in attesa di attivazione.
            </p>
            <p className="text-yellow-700 text-sm">
              Un amministratore deve attivare il tuo account prima che tu possa accedere alle funzionalità dell'applicazione.
            </p>
          </div>
          <div className="text-left bg-slate-50 rounded-lg p-4">
            <h3 className="font-medium text-slate-800 mb-2">Informazioni Account:</h3>
            <div className="space-y-1 text-sm text-slate-600">
              <p><span className="font-medium">Nome:</span> {profile.first_name} {profile.last_name}</p>
              <p><span className="font-medium">Email:</span> {profile.email}</p>
              <p><span className="font-medium">Stato:</span> <span className="text-yellow-600 font-medium">Registrato</span></p>
              <p><span className="font-medium">Registrato:</span> {new Date(profile.created_at).toLocaleDateString('it-IT')}</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Contatta un amministratore per richiedere l'attivazione del tuo account.
          </p>
        </motion.div>
      </div>
    );
  }

  // UPDATED: Check role-based access with viewer support
  if (requiredRole) {
    const hasRequiredRole = (() => {
      switch (requiredRole) {
        case 'admin':
          return isAdmin;
        case 'operator':
          // Allow admin, operator, AND viewer to access operator routes
          return isOperator || isAdmin || isViewer;
        case 'viewer':
          // Allow viewer, operator, and admin to access viewer routes
          return isViewer || isOperator || isAdmin;
        default:
          return false;
      }
    })();

    if (!hasRequiredRole) {
      // Show access denied if custom fallback not provided
      if (fallback) {
        return fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
            >
              <AlertTriangle className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-2xl font-bold text-slate-800 mb-4">
              Accesso Negato
            </h1>
            <p className="text-slate-600 mb-2">
              Non hai i permessi necessari per accedere a questa sezione.
            </p>
            <p className="text-sm text-slate-500">
              Il tuo ruolo attuale: <span className="font-medium capitalize">{profile.role}</span>
            </p>
            <p className="text-sm text-slate-500">
              Ruolo richiesto: <span className="font-medium capitalize">{requiredRole}</span>
            </p>
          </motion.div>
        </div>
      );
    }
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
}

// Convenience components for specific roles
export const AdminRoute = ({ children, fallback }) => (
  <ProtectedRoute requiredRole="admin" fallback={fallback}>
    {children}
  </ProtectedRoute>
);

// UPDATED: OperatorRoute now allows viewers too
export const OperatorRoute = ({ children, fallback }) => (
  <ProtectedRoute requiredRole="operator" fallback={fallback}>
    {children}
  </ProtectedRoute>
);

// NEW: ViewerRoute for routes that require at least viewer access
export const ViewerRoute = ({ children, fallback }) => (
  <ProtectedRoute requiredRole="viewer" fallback={fallback}>
    {children}
  </ProtectedRoute>
);

// UPDATED: Component for routes that should be accessible to active users (viewer, operator, admin), but not "registrato"
export const ActiveUserRoute = ({ children, fallback }) => {
  const { profile } = useAuth();
  
  if (profile?.role === 'registrato') {
    return <ProtectedRoute>{children}</ProtectedRoute>; // This will show the "registrato" message
  }
  
  return <>{children}</>;
};